<template>
  <b-card>
    <b-overlay
      :show="loading"
      variant="white"
      spinner-variant="primary"
      :opacity=".8"
    >
      <div class="d-flex justify-content-end">
        <b-dropdown
          id="dropdown-form"
          ref="dropdown"
          class="mr-1"
          right
          no-flip
          variant="secondary"
          text="Фильтр"
        >
          <b-dropdown-form
            class="py-1"
            @submit.prevent
          >
            <b-form-group
              label="По названию"
              label-for="dropdown-form-name"
              @submit.prevent.stop
            >
              <b-form-input
                id="dropdown-form-name"
                v-model="filters.name"
                size="sm"
              />
            </b-form-group>

            <b-form-group
              label="Город:"
              label-for="city_id"
            >
              <v-select
                v-model="filters.city_id"
                label="name"
                :reduce="option => option.id"
                :options="citiesList"
              />
            </b-form-group>

            <b-form-group
              label="Сеть:"
              label-for="network_id"
            >
              <v-select
                v-model="filters.network_id"
                label="name"
                :reduce="option => option.id"
                :options="clinicNetworksList"
              />
            </b-form-group>

            <b-form-group
              label="Cортировка по:"
              label-for="dropdown-form-sort_by"
              @submit.prevent.stop
            >
              <b-form-select
                id="dropdown-form-sort_by"
                v-model="filters.sort_by"
                size="sm"
                value-field="id"
                text-field="value"
                :options="[{id: null, value: '------'}, {id: 'id_asc', value: 'ID (от 0 до 99)'}, {id: 'id_desc', value: 'ID (от 99 до 0)'},{id: 'name_asc', value: 'Названию (от А до Я)'}, {id: 'name_desc', value: 'Названию (от Я до А)'}, {id: 'doctors_count_asc', value: 'По кол-ву врачей в клинике (от 0 до 99)'}, {id: 'doctors_count_desc', value: 'По кол-ву врачей в клинике (от 99 до 0)'}]"
              />
            </b-form-group>

            <b-form-group
              label="Кол-во на странице"
              label-for="dropdown-form-per-page"
              @submit.prevent.stop
            >
              <b-form-select
                id="dropdown-form-per-page"
                v-model="filters.perPage"
                size="sm"
                :options="[10, 20, 50, 100, 500]"
              />
            </b-form-group>

            <b-button-group>
              <b-button
                variant="primary"
                size="sm"
                @click="doApplyFilters"
              >
                Применить
              </b-button>
              <b-button
                variant="outline-secondary"
                size="sm"
                @click="doResetFilters"
              >
                Сбросить
              </b-button>
            </b-button-group>
          </b-dropdown-form>
        </b-dropdown>
        <router-link :to="{name: 'clinics-create'}">
          <b-button variant="primary">
            Создать
          </b-button>
        </router-link>
      </div>

      <data-table
        :columns="columns"
        :rows.sync="rows"
        :settings="settings"
        :dev-logs="false"
        @updateFilter="doUpdateFilter"
      >

        <template
          slot="table-column"
          slot-scope="props"
        >
          <div class="text-center">
            {{ props.column.label }}
          </div>
        </template>

        <template v-slot:table-row="item">
          <div
            v-if="item.column.field === 'action'"
            class="text-center"
          >
            <router-link :to="{name: 'clinics-update', params: {id: item.row.id}}">
              <b-button
                variant="flat-warning"
                class="btn-icon"
              >
                <feather-icon
                  icon="Edit3Icon"
                  class="text-warning"
                />
              </b-button>
            </router-link>

            <b-button
              variant="flat-danger"
              class="btn-icon"
              @click="objectDelete(item.row.id)"
            >
              <feather-icon
                icon="Trash2Icon"
                class="text-danger"
              />
            </b-button>
          </div>
          <div
            v-else
            class="text-center"
          >
            {{ item.formattedRow[item.column.field] }}
          </div>
        </template>
      </data-table>
    </b-overlay>
  </b-card>
</template>

<script>
import {
  BButton,
  BButtonGroup,
  BCard,
  BDropdown,
  BDropdownForm,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BOverlay,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import _ from 'lodash'
import DataTable from '@/components/DataTable.vue'
import axiosIns from '@/libs/axios'
import { rowsObjectConverter, settingsObjectConverter } from '@/helpers/dataTableHelper'
import dataTableMixin from '@/mixins/dataTableMixin'

export default {
  name: 'ClinicsList',
  components: {
    BCard,
    BFormGroup,
    BDropdown,
    BDropdownForm,
    BButton,
    BButtonGroup,
    BFormSelect,
    BOverlay,
    BFormInput,
    DataTable,
    vSelect,
  },
  mixins: [
    dataTableMixin,
  ],
  metaInfo: {
    title: 'Список клиник',
  },
  data() {
    return {
      loading: true,
      filterOnURL: false,
      columns: [
        {
          label: 'ID',
          field: 'id',
        },
        {
          label: 'Название',
          field: 'name',
        },
        {
          label: 'Город',
          field: rowObj => (rowObj.city_id !== null && rowObj.city_id ? this.getCityName(rowObj.city_id) : '—'),
        },
        {
          label: 'Сеть',
          field: rowObj => (rowObj.network_id !== null && rowObj.network_id ? this.getClinicNetworkName(rowObj.network_id) : '—'),
        },
        {
          label: 'Кол-во докторов',
          field: 'doctors_count',
        },
        {
          label: 'Действия',
          field: 'action',
        },
      ],
      citiesList: [],
      clinicNetworksList: [],
    }
  },
  beforeMount() {
    this.setDefaultFilter({
      sort_by: 'doctors_count_desc',
    }, () => { this.setFilterToDefault() })

    Promise.all([
      this.doLoadCities(),
      this.doLoadClinicNetworks(),
    ]).then(() => {
      Promise.all([
        this.doLoadClinics(),
      ]).then(() => {
        this.loading = false
      })
    })

    // Пушим промисы фильтров, чтоб вызывать методы
    this.filterPromises.push(() => this.doLoadClinics())
  },
  methods: {
    async doLoadClinics() {
      const res = await axiosIns({
        method: 'GET',
        url: '/clinics/list',
        params: _.merge(this.filters),
      })

      this.$set(this, 'settings', settingsObjectConverter(res.data.data))
      this.$set(this, 'rows', rowsObjectConverter(res.data.data))

      return res
    },
    async doLoadCities() {
      const res = await axiosIns({
        method: 'GET',
        url: '/cities/options',
      })

      this.citiesList = res.data.data

      return res
    },
    async doLoadClinicNetworks() {
      const res = await axiosIns({
        method: 'GET',
        url: '/clinic_networks/options',
      })

      this.clinicNetworksList = res.data.data

      return res
    },
    getCityName(cityId) {
      // eslint-disable-next-line radix
      return _.find(this.citiesList, city => city.id === parseInt(cityId))?.name ?? '—'
    },
    getClinicNetworkName(cityId) {
      // eslint-disable-next-line radix
      return _.find(this.clinicNetworksList, city => city.id === parseInt(cityId))?.name ?? '—'
    },
    async objectDelete(id) {
      const modalResult = await this.$swal({
        title: '',
        text: 'Вы уверены что хотите удалить эту запись?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Да',
        cancelButtonText: 'Нет',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      })

      if (modalResult.value) {
        await axiosIns({
          method: 'DELETE',
          url: `/clinics/${id}/delete`,
        })

        await this.doLoadClinics()
        this.$swal({
          icon: 'success',
          title: 'Удалено!',
          text: '',
          customClass: {
            confirmButton: 'btn btn-success',
          },
        })
      }
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
